import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Container from "@/components/containers/Container";
import FormSkeleton from "@/components/loadings/skeleton/FormSkeleton";
import type { ChildRouteContext } from "@/root";
import { HttpError } from "@/utils/permissions";

import { CreateCheckinPage } from "../../../../components/checkins/CreateCheckinPage";
import { fetchCheckinFormQueryOptions } from "../../../../services";

const searchSchema = z.object({
  objective_period_id: z.coerce.number(),
  user_id: z.coerce.number(),
});

export const Route = createFileRoute(
  "/_panel/_performance/performance/checkins/create",
)({
  component: RouteComponent,
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) =>
    ({
      crumb: [
        {
          label: context.t("Create Periodic Meeting", {
            ns: "performance.user",
          }),
        },
      ],
    }) satisfies ChildRouteContext,
  loaderDeps: ({ search }) => ({
    objective_period_id: search.objective_period_id,
    user_id: search.user_id,
  }),
  loader: ({ context, deps }) => {
    if (!deps.objective_period_id || !deps.user_id) {
      throw new HttpError(404, "Not Found");
    }
    return context.queryClient.ensureQueryData(
      fetchCheckinFormQueryOptions(deps.objective_period_id, deps.user_id),
    );
  },
  pendingComponent: FormSkeleton,
});

function RouteComponent() {
  const { t } = useTranslation("performance.user");
  const { objective_period_id, user_id } = Route.useSearch();

  if (!objective_period_id || !user_id) {
    throw new HttpError(404, "Not Found");
  }

  return (
    <Container
      title={t("Periodic Meetings")}
      description={t(
        "Set up regular meetings to continuously track performance and receive consistent feedback.",
      )}
      className="flex h-full flex-col"
    >
      <CreateCheckinPage
        objectivePeriodId={objective_period_id}
        userId={user_id}
      />
    </Container>
  );
}
